import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { WidgetCard } from './widget-card';

/**
 * Isolates one widget from the rest of the dashboard.
 *
 * The grid renders a dozen independent payloads side by side, so a widget that throws
 * while rendering has to degrade inside its own frame: the nearest boundary otherwise
 * sits above the router outlet, which takes the other cards — and the navigation — down
 * with it. Widget-level, not page-level, because the panels are independent of each other.
 */
interface WidgetBoundaryProps {
  /** Widget name, kept on the fallback card so a dead widget is still identifiable. */
  title?: string;
  children: ReactNode;
}

export function WidgetBoundary({ title, children }: WidgetBoundaryProps) {
  // Normalised here rather than as a parameter default, which only fires for `undefined` — the
  // drift this guards against can just as easily send `null` or a non-string.
  const label = typeof title === 'string' && title.trim() ? title : 'Widget';
  return <ErrorBoundary fallback={<WidgetErrorCard title={label} />}>{children}</ErrorBoundary>;
}

/** Card-sized fallback, so a failed widget keeps its place in the grid. */
function WidgetErrorCard({ title }: { title: string }) {
  return (
    <WidgetCard title={title} icon={AlertTriangle}>
      <p className="py-4 text-center text-xs text-muted-foreground">
        This widget couldn&apos;t be displayed.
      </p>
    </WidgetCard>
  );
}
